import DeleteOutlineOutlinedIcon from "@mui/icons-material/DeleteOutlineOutlined";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import PowerSettingsNewOutlinedIcon from "@mui/icons-material/PowerSettingsNewOutlined";
import SearchIcon from "@mui/icons-material/Search";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import { Box, Chip, CircularProgress, FormControl, IconButton, MenuItem, Pagination, Select } from "@mui/material";
import PropTypes from "prop-types";
import { forwardRef, useCallback, useEffect, useImperativeHandle, useRef, useState } from "react";
import { listEmailTemplateAction, publishEmailTemplate } from "../../Actions/EmailTemplateAction";
import { getPermissionData, getUserData } from "../../Actions/common";
import { CustomNoRowsOverlay } from "../../Assets/customTheme/CustomNoRowsOverlay";
import { DataGrid_SX } from "../../Assets/customTheme/constants";
import { default as CoustomTooltip, default as CustomTooltip } from "../../Assets/customTheme/customtooltip";
import { TableColumns, TableFilte, TableReset } from "../../Assets/svg/svgicon";
import { replacePlaceholders } from "../../utils/CommonFunctions";
import { formatDateTime } from "../Common/DateFormat";
import { ITEMS_PER_PAGE_OPTIONS, NETCLUES_URL, SUPPORT_EMAIL } from "../../config";
import CustomSnackbar from "../Common/snackBar";
import renderStatusBadge from "../../utils/badges";
import qbotLogoPng from "../../Assets/logo-white.svg";
import netcluesLogoGif from "../../Assets/netclues-logo.gif";
import { CommonTooltip } from "../../utils/tooltip";
import showConfirmation from "../../utils/Conformation";
import DataTable from "../../components/DataTable/DataTable"



const EmailTemplatesListing = ({ handleEdit, handleView }, ref) => {
  const [filterButtonDisabled, setFilterButtonDisabled] = useState(true);
  const [resetButtonDisabled, setResetButtonDisabled] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [shouldFetchData, setShouldFetchData] = useState(1);
  const [filterValues, setFilterValues] = useState({
    search: "",
    templateType: "all",
    status: "all",
  });
  const [selectedRows, setSelectedRows] = useState([]);
  const [data, setData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [totalCount, setTotalCount] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const htmlBodyDataRef = useRef("");
  const [selectedRowsPerPage, setSelectedRowsPerPage] = useState(ITEMS_PER_PAGE_OPTIONS[0]);

  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });
  const userData = localStorage.getItem("userData") ? getUserData() : null;
  const permissionData = localStorage.getItem(`${userData?.id}_permissionData`) ? getPermissionData(`${userData?.id}_permissionData`) : [];
  const hasPermission = permissionData[0]?.role_permissions?.find((m) => m?.module && m?.module?.slug === "templates");

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const fetchEmailTemplates = useCallback(async () => {
    setIsLoading(true);
    try {
      const response = await listEmailTemplateAction({
        page: currentPage,
        perPage: pageSize,
        searchQuery: filterValues.search || undefined,
        templateType: filterValues.templateType !== "all" ? filterValues.templateType : undefined,
        status: filterValues.status !== "all" ? filterValues.status : undefined,
      });
      setData(response?.data || []);
      htmlBodyDataRef.current = response?.htmlBody || "";
      setTotalCount(response?.totalCount || 0);
    } catch (err) {
      setSnackbar({ open: true, message: err?.message || "Failed to fetch email templates", severity: "error" });
    } finally {
      setIsLoading(false);
    }
  }, [currentPage, pageSize, filterValues]);

  useEffect(() => {
    fetchEmailTemplates();
  }, [fetchEmailTemplates]);

  useImperativeHandle(ref, () => ({
    refetchEmailTemplateListing: fetchEmailTemplates,
  }));



  const handleToggleStatus = async (id, isActive) => {
    await showConfirmation("toggleStatus", {
      entityName: "Template",
      entityId: id,
      currentStatus: isActive,
      action: async () => {
        // Your existing code inside action
        const { status, message } = await publishEmailTemplate({ id, isActive: !isActive });
        if (!status) {
          throw new Error(message || "Failed to update template status.");
        }
        return { status, message };
      },
      onSuccess: (res, newStatus) => {
        setSnackbar({
          open: true,
          message: res?.message || `Template ${newStatus ? "activated" : "deactivated"} successfully.`,
          severity: "success"
        });
        fetchEmailTemplates();
      },
      onError: (err) => {
        setSnackbar({
          open: true,
          message: err?.message || "Something went wrong while updating status.",
          severity: "error"
        });
      },
    });
  };


  const handleHtmlView = (row) => {
    const finalHtmlTemplate = replacePlaceholders(htmlBodyDataRef.current, {
      LOGO: qbotLogoPng,
      NETCLUES_LOGO: netcluesLogoGif,
      SUPPORT_EMAIL: userData?.settingData?.ADMIN_EMAIL || SUPPORT_EMAIL,
      NETCLUES_URL: NETCLUES_URL,
      BODY_DATA: row.body,
      CURRENT_YEAR: new Date().getFullYear(),
    });

    if (finalHtmlTemplate?.trim() !== "") handleView && handleView(finalHtmlTemplate);
  };



  const columns = [
    { field: "title", headerName: "Title", flex: 1 },
    { field: "subject", headerName: "Subject", flex: 1 },
    { field: "category", headerName: "Category", width: 140, renderCell: ({ row }) => row.category },
    {
      field: "isActive",
      headerName: "Status",
      width: 120,
      renderCell: ({ row }) => {
        const statusMap = { true: { text: "Active", status: "success" }, false: { text: "Inactive", status: "default" } };
        const { text, status } = statusMap[row?.isActive] || { text: "Unknown", status: "default" };
        return renderStatusBadge({ status, title: text });
      },
    },
    {
      field: "isTenantTemplate",
      headerName: "Template Type",
      width: 150,
      renderCell: ({ row }) => (
        <Chip
          label={row?.isTenantTemplate === false ? "Master" : "Tenant"}
          size="small"
          variant="outlined"
          sx={{ fontWeight: 600, borderRadius: "8px", color: row?.isTenantTemplate === false ? "primary.main" : "secondary.main" }}
        />
      ),
    },
    { field: "updatedAt", headerName: "Updated Date", width: 200, renderCell: ({ row }) => row.updatedAt ? formatDateTime(row.updatedAt, true) : "N/A" },
  ];

  const actions = {
    edit: hasPermission?.canEdit,
    view: hasPermission?.canView,
    activate: hasPermission?.canPublish,
    deactivate: hasPermission?.canPublish,
  };

  const handleAction = (type, row) => {
    if (type === "edit") handleEdit?.(row.id);
    if (type === "view") handleHtmlView(row);
    if (type === "activate" || type === "deactivate") handleToggleStatus(row.id, row.isActive);
  };

  const handleDeleteSelected = () => {
    if (selectedRows.length === 0) {
      return;
    }
  };

  const handleFilterData = () => {
    setIsLoading(true);
    setCurrentPage(1);
    setShouldFetchData(1);
    setFilterButtonDisabled(true);
    setResetButtonDisabled(false);
  };

  const handlePaginationChange = (event, value) => {
    setData([]);
    setSelectedRows([]);
    setCurrentPage(value);
    setShouldFetchData(1);
  };

  const handleRowsPerPageChange = (event) => {
    setData([]);
    setSelectedRows([]);
    const newValue = parseInt(event.target.value);
    setSelectedRowsPerPage(newValue);
    setPageSize(newValue);
    setCurrentPage(1);
    setIsLoading(true);
    setShouldFetchData(1);
  };

  return (
    <Box>
      <DataTable
        rows={data}
        columns={columns}
        loading={isLoading}
        totalCount={totalCount}
        page={currentPage}
        pageSize={pageSize}
        filters={[
          { type: "search", name: "search", placeholder: "Search templates" },
          {
            type: "select", name: "templateType", label: "Template Type", options: [
              { value: "all", label: "All Template" },
              { value: "1", label: "Master" },
              { value: "0", label: "Tenant" },
            ]
          },
          {
            type: "select", name: "status", label: "Status", options: [
              { value: "all", label: "All Status" },
              { value: "1", label: "Active" },
              { value: "0", label: "Inactive" },
            ]
          }
        ]}
        filterValues={filterValues}
        actions={actions}
        onAction={handleAction}
        onFilterApply={setFilterValues}
        onFilterReset={() => setFilterValues({ search: "", templateType: "all", status: "all" })}
        onPageChange={setCurrentPage}
        onPageSizeChange={setPageSize}
      />
      <CustomSnackbar
        open={snackbar.open}
        message={snackbar.message}
        severity={snackbar.severity}
        onClose={handleCloseSnackbar}
      />
    </Box>
  );
};

export default forwardRef(EmailTemplatesListing);

EmailTemplatesListing.propTypes = {
  handleEdit: PropTypes.func.isRequired,
  handleView: PropTypes.func,
};
